import React, { useMemo, useState, useRef } from "react";
import carSvg from "../assets/tyre-board-car.svg";
import { Camera, X, Check } from 'lucide-react';

export default function TyreOverlaySVG({ form, setForm, onBonnetClick }) {
  const [active, setActive] = useState(null);
  const fileInputRef = useRef(null);

  const VB = { w: 1210, h: 828 };
  const SHOW_GUIDES = true;

  const hotspots = useMemo(() => ({
    nsf: { x: 340, y: 100, w: 86, h: 165, label: "Near-Side Front", idx: 0, pos: "Front Left" },
    osf: { x: 740, y: 100, w: 86, h: 165, label: "Off-Side Front", idx: 1, pos: "Front Right" },
    nsr: { x: 340, y: 450, w: 86, h: 165, label: "Near-Side Rear", idx: 2, pos: "Rear Left" },
    osr: { x: 740, y: 450, w: 86, h: 165, label: "Off-Side Rear", idx: 3, pos: "Rear Right" },
    spare: { x: 530, y: 705, r: 58, label: "Spare (if present)" },
    bonnet: { x: 445, y: 40, w: 275, h: 340, label: "🔧 Mechanical Checks" }, // Bonnet hotspot - positioned on actual bonnet
  }), []);

  const td = (i) => (i != null ? form.tyres?.[i]?.treadDepth || "" : "");
  const wc = (i) => (i != null ? form.tyres?.[i]?.condition || "" : "");

  const handlePhotoUpload = async (e) => {
    if (!active || hotspots[active].idx == null) return;
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const t = [...(form.tyres || [])];
        const idx = hotspots[active].idx;
        t[idx] = { ...(t[idx] || {}), photo: reader.result };
        setForm({ ...form, tyres: t });
      };
      reader.readAsDataURL(file);
    }
  };

  const save = (id, depth, condition) => {
    const h = hotspots[id];
    if (h.idx == null) return;
    const t = [...(form.tyres || [])];
    t[h.idx] = { ...(t[h.idx] || {}), position: h.pos, treadDepth: depth, condition };
    setForm({ ...form, tyres: t });
    setActive(null);
  };

  const hasTyreData = (idx) => {
    const tyre = form.tyres?.[idx];
    return tyre?.treadDepth || tyre?.condition;
  };

  return (
    <>
      {/* SVG Car with Tyres */}
      <div className="tyre-stage" style={{ width: '100%' }}>
        <svg className="tyre-svg" viewBox={`0 0 ${VB.w} ${VB.h}`} role="img" aria-label="Tyre diagram">
          <image href={carSvg} x="0" y="0" width={VB.w} height={VB.h} />

          {SHOW_GUIDES && (
            <>
              {["nsf", "osf", "nsr", "osr"].map((id) => {
                const h = hotspots[id];
                const hasData = hasTyreData(h.idx);
                return (
                  <g key={`g-${id}`} pointerEvents="none">
                    <rect
                      className="guide-rect"
                      x={h.x} y={h.y} width={h.w} height={h.h} rx="10" ry="10"
                      style={{
                        fill: hasData ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                        stroke: hasData ? '#22c55e' : '#3b82f6',
                        strokeWidth: 3
                      }}
                    />
                    <text
                      className="guide-label"
                      x={h.x + h.w / 2}
                      y={h.y - 8}
                      textAnchor="middle"
                      style={{ fill: hasData ? '#22c55e' : '#3b82f6', fontWeight: 'bold' }}
                    >
                      {id.toUpperCase()} {hasData && '✓'}
                    </text>
                  </g>
                );
              })}
              <g pointerEvents="none">
                <circle
                  className="guide-circle"
                  cx={hotspots.spare.x + hotspots.spare.r}
                  cy={hotspots.spare.y + hotspots.spare.r}
                  r={hotspots.spare.r}
                  style={{ fill: 'rgba(156, 163, 175, 0.2)', stroke: '#9ca3af', strokeWidth: 2 }}
                />
                <text
                  className="guide-label"
                  x={hotspots.spare.x + hotspots.spare.r}
                  y={hotspots.spare.y - 6}
                  textAnchor="middle"
                  style={{ fill: '#6b7280' }}
                >
                  Spare
                </text>
              </g>

              {/* Bonnet/Hood Guide */}
              <g pointerEvents="none">
                <rect
                  className="guide-rect"
                  x={hotspots.bonnet.x}
                  y={hotspots.bonnet.y}
                  width={hotspots.bonnet.w}
                  height={hotspots.bonnet.h}
                  rx="10"
                  ry="10"
                  style={{
                    fill: 'rgba(239, 68, 68, 0.1)',
                    stroke: '#ef4444',
                    strokeWidth: 2,
                    strokeDasharray: '8 4'
                  }}
                />
                <text
                  className="guide-label"
                  x={hotspots.bonnet.x + hotspots.bonnet.w / 2}
                  y={hotspots.bonnet.y + hotspots.bonnet.h / 2}
                  textAnchor="middle"
                  style={{ fill: '#ef4444', fontWeight: 'bold', fontSize: '18px' }}
                >
                  {hotspots.bonnet.label}
                </text>
              </g>
            </>
          )}

          {/* Clickable hotspots */}
          <rect
            className="hotspot"
            x={hotspots.nsf.x} y={hotspots.nsf.y} width={hotspots.nsf.w} height={hotspots.nsf.h}
            rx="10" onClick={() => setActive("nsf")} fill="transparent"
            style={{ cursor: 'pointer' }}
          />
          <rect
            className="hotspot"
            x={hotspots.osf.x} y={hotspots.osf.y} width={hotspots.osf.w} height={hotspots.osf.h}
            rx="10" onClick={() => setActive("osf")} fill="transparent"
            style={{ cursor: 'pointer' }}
          />
          <rect
            className="hotspot"
            x={hotspots.nsr.x} y={hotspots.nsr.y} width={hotspots.nsr.w} height={hotspots.nsr.h}
            rx="10" onClick={() => setActive("nsr")} fill="transparent"
            style={{ cursor: 'pointer' }}
          />
          <rect
            className="hotspot"
            x={hotspots.osr.x} y={hotspots.osr.y} width={hotspots.osr.w} height={hotspots.osr.h}
            rx="10" onClick={() => setActive("osr")} fill="transparent"
            style={{ cursor: 'pointer' }}
          />
          <circle
            className="hotspot"
            cx={hotspots.spare.x + hotspots.spare.r}
            cy={hotspots.spare.y + hotspots.spare.r}
            r={hotspots.spare.r}
            onClick={() => setActive("spare")} fill="transparent"
            style={{ cursor: 'pointer' }}
          />

          {/* Bonnet Clickable Hotspot */}
          <rect
            className="hotspot"
            x={hotspots.bonnet.x}
            y={hotspots.bonnet.y}
            width={hotspots.bonnet.w}
            height={hotspots.bonnet.h}
            rx="10"
            onClick={() => onBonnetClick && onBonnetClick()}
            fill="transparent"
            style={{ cursor: 'pointer' }}
          />
        </svg>
      </div>

      {/* POPUP FOR TYRE DETAILS */}
      {active && hotspots[active].idx != null && (
        <div
          className="sub-part-backdrop"
          onClick={() => setActive(null)}
          style={{ zIndex: 10000 }}
        >
          <div
            className="sub-part-popup scrollable-popup"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px', width: '90%' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                {hotspots[active].label}
              </h3>
              <button
                onClick={() => setActive(null)}
                style={{
                  padding: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '8px'
                }}
                className="hover:bg-gray-100"
                type="button"
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Tread Depth (mm)
              </label>
              <select
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                defaultValue={td(hotspots[active].idx)}
                id="tp-depth"
              >
                <option value="">Select</option>
                <option value="1.6">1.6 (Legal Min)</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8 (New)</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Wheel Condition
              </label>
              <input
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                defaultValue={wc(hotspots[active].idx)}
                id="tp-cond"
                placeholder="e.g., Good, Scuffs, Dents"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Photo
              </label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-4 py-2 border-2 border-dashed rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                type="button"
              >
                <Camera size={20} />
                {form.tyres?.[hotspots[active].idx]?.photo ? 'Change Photo' : 'Add Photo'}
              </button>
              {form.tyres?.[hotspots[active].idx]?.photo && (
                <img
                  src={form.tyres[hotspots[active].idx].photo}
                  alt="Tyre"
                  className="mt-2 w-full h-32 object-cover rounded-lg"
                />
              )}
            </div>

            <button
              type="button"
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              onClick={() => {
                const depth = document.getElementById("tp-depth").value;
                const cond = document.getElementById("tp-cond").value;
                save(active, depth, cond);
              }}
            >
              <Check size={20} />
              Save Tyre Data
            </button>
          </div>
        </div>
      )}
    </>
  );
}
